import {
  ArrowLeftRight, Building2, CalendarDays, Camera, ChartLine, ClipboardCheck, FileCheck2, FolderTree, ListChecks, ListTodo, Mail, Plug,
  Send, ServerCog, Table2, UserCog, Users,
} from "lucide-react";
import type { CurrentUser } from "../auth/useCurrentUser";
import { frameworkColor, frameworkLabel } from "./frameworkMeta";
import type { NavContextBlock, NavModel, NavSection } from "./types";

// ── Auditor shell navigation ──────────────────────────────────────────────
// Builds the model of docs/NAVIGATION.md section 6. Bureau-admin items are
// hidden for everyone else (the API enforces requireBureauAdmin regardless).
// With a client selected the engagement context block and an "Audit" section
// sit above the workspace sections.

/** The engaged client the auditor is looking at (from GET /auditor/clients). */
export interface AuditorNavClient {
  id: string;
  name: string;
  sector?: string | null;
  /** Standards engaged with this bureau, within the auditor's live scope. */
  frameworkIds: string[];
  /** Latest compliance score, null when no check has run. */
  score?: number | null;
  /** False when the client's edition leaves CIS hardening out (the Jersey edition). */
  cisHardening?: boolean;
}

export interface AuditorNavInput {
  user: CurrentUser;
  /**
   * The organisation id from /auditor/clients/:orgId/*. The "Audit" section
   * needs only this, so it is there from the first render of a deep link.
   */
  selectedOrgId?: string | null;
  /** The same client's summary once known; renders the engagement context block. */
  selectedClient?: AuditorNavClient | null;
  /** The bureau's display name (user.bureauName with the list fallbacks applied). */
  bureauName?: string | null;
  /** Present for consultancy staff acting as auditors: the way back to the consultant workspace. */
  onSwitchToConsultant?: () => void;
  /** The bureau's kind (GET /auditor/clients); a competent authority gets the Submissions tab. */
  bureauKind?: string | null;
}

export const AUDITOR_HOME = "/auditor/clients";

/** The per-client audit pages. */
export type AuditTab = "evidence-map" | "controls" | "registers" | "snapshots" | "sampling" | "hardening" | "technical-verification" | "submissions";

/** Shell-relative paths of the per-client audit pages. */
export function auditClientPath(orgId: string, tab: AuditTab = "evidence-map"): string {
  return `/auditor/clients/${orgId}/${tab}`;
}

export const AUDIT_TAB_LABELS: Record<AuditTab, string> = {
  "evidence-map": "Evidence Map",
  controls:       "Controls & Findings",
  registers:      "Registers",
  snapshots:      "Audit Snapshots",
  sampling:       "Sampling",
  hardening:      "CIS Hardening",
  "technical-verification": "Technical Verification",
  submissions:    "Submissions",
};

export function auditorContextBlock(client: AuditorNavClient): NavContextBlock {
  return {
    initial: (client.name.trim()[0] ?? "?").toUpperCase(),
    title: client.name,
    subtitle: client.sector ?? undefined,
    dots: client.frameworkIds.map((id) => ({ color: frameworkColor(id), label: frameworkLabel(id) })),
    score: client.score ?? null,
    backTo: AUDITOR_HOME,
    backLabel: "All clients",
  };
}

export function buildAuditorNav({ user, selectedOrgId, selectedClient, bureauName, onSwitchToConsultant, bureauKind }: AuditorNavInput): NavModel {
  const isAdmin = user.isBureauAdmin;
  const sections: NavSection[] = [];
  const auditOrgId = selectedOrgId ?? selectedClient?.id ?? null;

  if (auditOrgId) {
    sections.push({
      id: "audit",
      label: "Audit",
      items: [
        { id: "audit:evidence-map", label: AUDIT_TAB_LABELS["evidence-map"], to: auditClientPath(auditOrgId, "evidence-map"), icon: FolderTree, keywords: ["evidence", "documents", "tree", "export"] },
        { id: "audit:controls",     label: AUDIT_TAB_LABELS.controls,        to: auditClientPath(auditOrgId, "controls"),     icon: ClipboardCheck, keywords: ["findings", "clauses", "controls", "soa"] },
        // The client's live registers, limited by the API to those the engaged standards draw on.
        { id: "audit:registers",    label: AUDIT_TAB_LABELS.registers,       to: auditClientPath(auditOrgId, "registers"),    icon: Table2, keywords: ["registers", "risks", "incidents", "suppliers", "assets", "training", "corrective actions", "continuity"] },
        { id: "audit:snapshots",    label: AUDIT_TAB_LABELS.snapshots,       to: auditClientPath(auditOrgId, "snapshots"),    icon: Camera, keywords: ["snapshot", "as at", "audit date", "frozen", "evidence versions"] },
        { id: "audit:sampling",     label: AUDIT_TAB_LABELS.sampling,        to: auditClientPath(auditOrgId, "sampling"),     icon: ListChecks, keywords: ["sample", "sampling", "test", "exception", "leavers", "incidents"] },
        { id: "audit:hardening",    label: AUDIT_TAB_LABELS.hardening,       to: auditClientPath(auditOrgId, "hardening"),    icon: ServerCog, keywords: ["cis", "benchmarks", "hardening"],
          hidden: selectedClient?.id === auditOrgId && selectedClient.cisHardening === false },
        // CE+ engaged and in this auditor's scope: the assessor's technical audit (the API checks the scope again).
        { id: "audit:technical-verification", label: AUDIT_TAB_LABELS["technical-verification"], to: auditClientPath(auditOrgId, "technical-verification"), icon: FileCheck2,
          hidden: !(selectedClient?.id === auditOrgId && selectedClient.frameworkIds.includes("cyber_essentials_plus")), keywords: ["cyber essentials plus", "ce+", "scan", "testing", "verification"] },
        // A competent authority reads the submissions addressed to it (the API refuses a certification body).
        { id: "audit:submissions",  label: AUDIT_TAB_LABELS.submissions,     to: auditClientPath(auditOrgId, "submissions"),  icon: Send,
          hidden: bureauKind !== "competent_authority", keywords: ["submissions", "notifications", "acknowledge", "reference", "regulator"] },
      ],
    });
  }

  sections.push({
    id: "workspace",
    label: "Workspace",
    items: [
      // Exact: /auditor/clients/:orgId/* belongs to the "Audit" section rows.
      { id: "clients",   label: "My Clients", to: AUDITOR_HOME,        exact: true, icon: Users, badge: "clientsPendingReview", keywords: ["clients", "organisations", "engagements", "pending review"] },
      { id: "actions",   label: "My Actions", to: "/auditor/actions",   icon: ListTodo,     badge: "myActions", keywords: ["tasks", "verify", "findings"] },
      { id: "calendar",  label: "Calendar",   to: "/auditor/calendar",  icon: CalendarDays, keywords: ["schedule", "audits", "dates", "overdue"] },
      { id: "analytics", label: "Analytics",  to: "/auditor/analytics", icon: ChartLine,    keywords: ["portfolio", "scores", "gaps", "sectors"] },
    ],
  });

  sections.push({
    id: "bureau",
    label: "Bureau",
    items: [
      { id: "bureau:profile",      label: "Bureau Profile", to: "/auditor/bureau/profile",      icon: Building2, keywords: ["accreditation", "standards", "marketplace", "pricing"] },
      { id: "bureau:team",         label: "Team & Access",  to: "/auditor/bureau/team",         icon: UserCog,   hidden: !isAdmin, keywords: ["members", "roles", "specialist", "framework access"] },
      { id: "bureau:invitations",  label: "Invitations",    to: "/auditor/bureau/invitations",  icon: Mail,      hidden: !isAdmin, keywords: ["invite", "auditor", "join"] },
      { id: "bureau:integrations", label: "Integrations",   to: "/auditor/bureau/integrations", icon: Plug,      hidden: !isAdmin, keywords: ["connectors", "certification body", "webhooks"] },
    ],
  });

  if (onSwitchToConsultant) {
    sections.push({
      id: "switch",
      label: "Switch workspace",
      items: [
        { id: "switch:consultant", label: "Consultant workspace", onSelect: onSwitchToConsultant, icon: ArrowLeftRight, keywords: ["consultant", "portfolio", "switch"] },
      ],
    });
  }

  return {
    shellKey: "auditor",
    header: {
      title: bureauName ?? user.bureauName ?? "Abacas",
      subtitle: bureauName || user.bureauName ? "Certification bureau" : "Security",
      role: { label: "Auditor", tone: "auditor" },
    },
    context: selectedClient && selectedClient.id === auditOrgId ? auditorContextBlock(selectedClient) : undefined,
    sections,
  };
}
